<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { LocaleSchema } from '@/locales/index';

defineOptions({
  name: 'AppPagination'
});

const { t } = useI18n<{message: LocaleSchema}>({
  useScope: 'global',
});

interface Props {
  currentPage: number
  totalItems: number
  itemsPerPage: number
  maxVisiblePages?: number
}

interface Emits {
  pageChanged: [page: number]
}

const props = withDefaults(defineProps<Props>(), {
  maxVisiblePages: 5
});

const emit = defineEmits<Emits>();

const totalPages = computed(() => Math.ceil(props.totalItems / props.itemsPerPage));

const visiblePages = computed(() => {
  const total = totalPages.value;
  const current = props.currentPage;
  const maxVisible = props.maxVisiblePages;
  
  if (total <= maxVisible) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  
  const half = Math.floor(maxVisible / 2);
  let start = Math.max(1, current - half);
  const end = Math.min(total, start + maxVisible - 1);
  
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1);
  }
  
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

const canGoToPrevious = computed(() => props.currentPage > 1);
const canGoToNext = computed(() => props.currentPage < totalPages.value);

const showFirstPage = computed(() => visiblePages.value[0] > 1);
const showLastPage = computed(() => visiblePages.value[visiblePages.value.length - 1] < totalPages.value);

const showFirstEllipsis = computed(() => visiblePages.value[0] > 2);
const showLastEllipsis = computed(() => visiblePages.value[visiblePages.value.length - 1] < totalPages.value - 1);

function goToPage(page: number) {
  if (page >= 1 && page <= totalPages.value && page !== props.currentPage) {
    emit('pageChanged', page);
  }
}

function goToPrevious() {
  if (canGoToPrevious.value) {
    goToPage(props.currentPage - 1);
  }
}

function goToNext() {
  if (canGoToNext.value) {
    goToPage(props.currentPage + 1);
  }
}
</script>

<template>
  <div class="pagination-container" v-if="totalPages > 1">
    <!-- Navigation de pagination -->
    <nav class="pagination" role="navigation" aria-label="Pagination">
      <!-- Bouton Précédent -->
      <button type="button" class="pagination-btn pagination-prev" :disabled="!canGoToPrevious" @click="goToPrevious" :aria-label="t('pagination.previous')">
        {{ t('pagination.previous') }}
      </button>
      
      <!-- Première page -->
      <button v-if="showFirstPage" type="button" class="pagination-btn pagination-page" @click="goToPage(1)" :aria-label="t('pagination.goToPage', { page: 1 })">
        1
      </button>
      
      <!-- Ellipsis début -->
      <span v-if="showFirstEllipsis" class="pagination-ellipsis" aria-hidden="true">
        ...
      </span>
      
      <!-- Pages visibles -->
      <button v-for="page in visiblePages" :key="page" type="button" class="pagination-btn pagination-page" :class="{ 'pagination-page--active': page === currentPage }" @click="goToPage(page)" :aria-label="t('pagination.goToPage', { page })" :aria-current="page === currentPage ? 'page' : undefined">
        {{ page }}
      </button>
      
      <!-- Ellipsis fin -->
      <span v-if="showLastEllipsis" class="pagination-ellipsis" aria-hidden="true">
        ...
      </span>
      
      <!-- Dernière page -->
      <button v-if="showLastPage" type="button" class="pagination-btn pagination-page" @click="goToPage(totalPages)" :aria-label="t('pagination.goToPage', { page: totalPages })">
        {{ totalPages }}
      </button>
      
      <!-- Bouton Suivant -->
      <button type="button" class="pagination-btn pagination-next" :disabled="!canGoToNext" @click="goToNext" :aria-label="t('pagination.next')">
        {{ t('pagination.next') }}
      </button>
    </nav>
  </div>
</template>

<style scoped>
.pagination-container {
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  align-items: center;
  margin: 3.2rem 0;
}

.pagination-info {
  font-size: 1.4rem;
  color: var(--text-color, #666);
  text-align: center;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex-wrap: wrap;
  justify-content: center;
}

.pagination-btn {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 1.2rem 1.6rem;
  border: 1px solid var(--gray-200, #A6B3A2);
  background: #ffffff;
  color: var(--text-color, #333);
  text-decoration: none;
  border-radius: 0.8rem;
  font-size: 1.6rem;
  font-weight: 600;
  transition: all 0.2s ease;
  cursor: pointer;
  min-width: 4rem;
  justify-content: center;
}

.pagination-btn:hover:not(:disabled) {
  background: #e8f5e8;
  border-color: #57B84A;
  color: #57B84A;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f9fafb;
  color: #9ca3af;
  border-color: #e5e7eb;
}

.pagination-page--active {
  background: #57B84A;
  color: #ffffff;
  border-color: #57B84A;
  font-weight: 600;
}

.pagination-page--active:hover {
  background: #4a9a3d;
  border-color: #4a9a3d;
  color: #ffffff;
}

.pagination-ellipsis {
  display: flex;
  align-items: center;
  padding: 1.2rem 0.8rem;
  color: var(--text-color, #666);
  font-weight: 500;
  font-size: 1.6rem;
}

.pagination-prev .icon,
.pagination-next .icon {
  width: 1.6rem;
  height: 1.6rem;
  fill: currentColor;
}

@media (max-width: 1024px) {
  .pagination-container {
    margin: 2.4rem 0;
    gap: 1.2rem;
  }
  
  .pagination {
    gap: 0.4rem;
  }
  
  .pagination-btn {
    padding: 1.0rem 1.2rem;
    font-size: 1.4rem;
    min-width: 3.6rem;
    gap: 0.6rem;
  }
  
  .pagination-prev,
  .pagination-next {
    gap: 0.6rem;
  }
  
  .pagination-info {
    font-size: 1.2rem;
  }

  .pagination-ellipsis {
    padding: 1.0rem 0.6rem;
    font-size: 1.4rem;
  }

  .pagination-prev .icon,
  .pagination-next .icon {
    width: 1.4rem;
    height: 1.4rem;
  }
}

@media (max-width: 640px) {
  .pagination-container {
    margin: 2rem 0;
  }
  
  .pagination {
    gap: 0.2rem;
  }
  
  .pagination-btn {
    padding: 0.8rem 1rem;
    font-size: 1.2rem;
    min-width: 3.2rem;
  }
  
  .pagination-prev,
  .pagination-next {
    gap: 0.4rem;
  }
  
  .pagination-info {
    font-size: 1rem;
  }
}
</style>